import React, { useMemo } from 'react'
import {
  Paper,
  Box,
  InputAdornment,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import Search from '@mui/icons-material/Search'

import { TextInput } from '../'

//Basic default functionality
const defaultRenderElement = (element, chosen, keyField, nameField) => (
  <ListItem key={element[keyField]}>
    <FormControlLabel
      label={element[nameField]}
      control={<Checkbox checked={chosen} disableRipple />}
    />
  </ListItem>
)

const defaultRenderContainer = ({ children }) => <List>{children}</List>

const defaultFilter = (element, filter, nameField) => {
  return element[nameField].toUpperCase().indexOf(filter) > -1
}

/**
 * @typedef {object} CardCheckListProps
 * @property {string} [className] - Clase CSS adicional para el Paper contenedor.
 * @property {string} [keyField='key'] - Campo del objeto que actúa como identificador único.
 * @property {string} [nameField='name'] - Campo del objeto que se muestra como etiqueta.
 * @property {Array<object>} [elements=[]] - Array de elementos a mostrar en la lista.
 * @property {Array<string|number>} [selected=[]] - Array con las claves de los elementos seleccionados.
 * @property {function(object, boolean, string, string):React.ReactNode} [funcRenderElement] - Función para renderizar cada elemento.
 * @property {function({children: React.ReactNode}):React.ReactNode} [funcRenderContainer] - Función para renderizar el contenedor de la lista.
 * @property {boolean} [searchBox] - Si es true, muestra un campo de búsqueda.
 * @property {string} [searchValue] - Valor actual del campo de búsqueda.
 * @property {string} [searchPlaceholder=''] - Placeholder del campo de búsqueda.
 * @property {React.Component} [searchIcon=Search] - Ícono mostrado en el campo de búsqueda.
 * @property {function(object, string, string):boolean} [funcSearchFilter] - Función para filtrar elementos según el valor de búsqueda.
 * @property {function(event):void} [funcSearchOnChange] - Función invocada al cambiar el valor de búsqueda.
 * @property {boolean} [wholeSelector] - Si es true, muestra un checkbox para seleccionar/deseleccionar todos.
 * @property {string} [wholeCaption='ALL'] - Etiqueta del checkbox de selección total.
 * @property {function(event, Array<string|number>):void} [funcWholeOnChange] - Función invocada al cambiar el checkbox de selección total.
 */

/**
 * Lista de elementos con checkboxes, búsqueda y selección total.
 *
 * Componente Paper que renderiza una lista de elementos con checkboxes, con soporte para:
 * búsqueda integrada, selección/deselección total, renderizado personalizado de elementos
 * y contenedor, y filtrado dinámico. Gestiona el estado de selección externamente.
 *
 * @component
 * @example
 * const [selected, setSelected] = useState([]);
 * const elements = [
 *   { key: 1, name: 'Elemento 1' },
 *   { key: 2, name: 'Elemento 2' }
 * ];
 * <CardCheckList
 *   elements={elements}
 *   selected={selected}
 *   searchBox={true}
 *   searchValue={searchTerm}
 *   funcSearchOnChange={(e) => setSearchTerm(e.target.value)}
 *   wholeSelector={true}
 *   funcWholeOnChange={(e, keys) => setSelected(e.target.checked ? keys : [])}
 * />
 *
 * @param {CardCheckListProps} props - Las props del componente CardCheckList.
 */
//Component Definition:
export const CardCheckList = ({
  className,
  //Main input:
  keyField = 'key',
  nameField = 'name',
  elements = [],
  selected = [],
  funcRenderElement = defaultRenderElement,
  funcRenderContainer = defaultRenderContainer,
  //Optional Search box functionality
  searchBox,
  searchValue,
  searchPlaceholder = '',
  searchIcon = Search,
  funcSearchFilter = defaultFilter,
  funcSearchOnChange = (event) => {},
  //Optional Whole Selection checkbox functionality
  wholeSelector,
  wholeCaption = 'ALL',
  funcWholeOnChange = (event, keys) => {},
}) => {
  //List to display: we filter element's nameField according to searchValue and render according to the given function
  const [elementsDisplay, elementsChosen, renderedList] = useMemo(() => {
    const elementsChosen = []
    const renderedList = []
    const elementsDisplay = []
    elements.forEach((el) => {
      if (searchValue && !funcSearchFilter(el, searchValue, nameField)) {
        return
      }
      const chosen = selected.indexOf(el[keyField]) > -1
      if (chosen) {
        elementsChosen.push(el)
      }
      renderedList.push(funcRenderElement(el, chosen, keyField, nameField))
      elementsDisplay.push(el[keyField])
      return
    })
    return [elementsDisplay, elementsChosen, renderedList]
  }, [selected, searchValue, elements, nameField, keyField, funcSearchFilter, funcRenderElement])

  //List container component
  const MyContainer = funcRenderContainer

  //Search box:
  let searchBoxJSX = null
  if (searchBox) {
    const SearchIcon = searchIcon
    searchBoxJSX = (
      <Box p={3}>
        <TextInput
          name="_search"
          style={{ backgroundColor: 'white' }}
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={funcSearchOnChange}
          InputProps={{
            startAdornment: (
              <InputAdornment style={{ marginRight: '1em' }}>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    )
  }

  //Whole selector:
  let wholeSelectorJSX = null
  if (wholeSelector) {
    const wholeNum = elementsDisplay.length
    const wholeChosen = elementsChosen.length
    const isWhole = wholeNum === wholeChosen
    wholeSelectorJSX = (
      <Box p={3}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={isWhole}
              indeterminate={wholeChosen > 0 && !isWhole}
              onChange={(event) => funcWholeOnChange(event, elementsDisplay)}
              disableRipple
            />
          }
          label={wholeCaption + ' (' + wholeNum + ')'}
        />
        <hr />
      </Box>
    )
  }

  //Full component:
  return (
    <Paper elevation={2} className={className}>
      {searchBoxJSX}
      {wholeSelectorJSX}
      <MyContainer>{renderedList}</MyContainer>
    </Paper>
  )
}

export default CardCheckList
